<template>
  <div class="qh-page access-admin-page">
    <header class="admin-header">
      <div>
        <span class="admin-kicker">Administración</span>
        <h1 class="qh-page-title">Perfiles y accesos</h1>
        <p class="qh-page-subtitle">Define perfiles y relaciona las categorías que cada tipo de usuario podrá visualizar.</p>
      </div>

      <div class="admin-stats">
        <div class="stat-card">
          <strong>{{ totalPerfiles }}</strong>
          <span>Perfiles</span>
        </div>
        <div class="stat-card">
          <strong>{{ totalAsociaciones }}</strong>
          <span>Asociaciones</span>
        </div>
      </div>
    </header>

    <section class="actions-guide">
      <strong>Acciones disponibles</strong>
      <span><i class="bi bi-toggle-on text-success"></i> Activar</span>
      <span><i class="bi bi-toggle-off text-secondary"></i> Desactivar</span>
      <span><i class="bi bi-pencil-square text-warning"></i> Editar</span>
      <span><i class="bi bi-link-45deg text-primary"></i> Asociar</span>
      <span><i class="bi bi-trash3 text-danger"></i> Eliminar</span>
    </section>

    <nav class="admin-tabs" aria-label="Secciones de perfiles y accesos">
      <button type="button" :class="{ active: activeTab === 'perfiles' }" @click="activeTab = 'perfiles'">
        <i class="bi bi-person-badge"></i>
        <span>Perfiles</span>
        <small>{{ totalPerfiles }}</small>
      </button>

      <button type="button" :class="{ active: activeTab === 'categorias' }" @click="activeTab = 'categorias'">
        <i class="bi bi-diagram-3"></i>
        <span>Categorías por perfil</span>
        <small>{{ totalAsociaciones }}</small>
      </button>
    </nav>

    <div class="container-fluid p-0">
      <section v-show="activeTab === 'perfiles'" class="tab-content-panel">
        <section class="qh-panel">
          <div class="qh-panel-header">
            <div class="d-flex align-items-center gap-3">
              <span class="qh-section-icon"><i class="bi bi-person-plus"></i></span>
              <div>
                <h2 class="qh-panel-title">Crear perfil</h2>
                <p class="qh-panel-copy">Registra un nuevo perfil para organizar los permisos del sistema.</p>
              </div>
            </div>
          </div>

          <div class="qh-panel-body">
            <form @submit.prevent="GuardarPerfil" class="profile-form">
              <div class="profile-field">
                <label class="qh-label" for="perfilNombre"><i class="bi bi-person-badge"></i> Nombre del perfil</label>
                <input id="perfilNombre" type="text" class="form-control" required v-model.trim="perfilNombre" placeholder="Ej. Supervisor moderno" />
                <small class="qh-help">Este nombre será visible al asignar permisos a los usuarios.</small>
              </div>

              <button class="btn qh-btn qh-btn-primary profile-save" :disabled="!perfilNombre.trim()">
                <i class="bi bi-check2-circle"></i>
                <span>Crear perfil</span>
              </button>
            </form>
          </div>
        </section>

        <section class="qh-panel">
          <div class="qh-panel-header">
            <div>
              <h2 class="qh-panel-title">Perfiles registrados</h2>
              <p class="qh-panel-copy">Administra el nombre y estado de los perfiles existentes.</p>
            </div>
          </div>
          <div class="qh-panel-body">
            <TablaPerfil />
          </div>
        </section>
      </section>

      <section v-show="activeTab === 'categorias'" class="tab-content-panel">
        <section class="qh-panel association-panel">
          <div class="qh-panel-header">
            <div class="d-flex align-items-center gap-3">
              <span class="qh-section-icon"><i class="bi bi-link-45deg"></i></span>
              <div>
                <h2 class="qh-panel-title">Asociar categoría a perfil</h2>
                <p class="qh-panel-copy">Selecciona la categoría y el perfil que tendrá acceso a ella.</p>
              </div>
            </div>
          </div>

          <div class="qh-panel-body">
            <form @submit.prevent="CategoriaxPerfil" class="association-form">
              <div>
                <label class="qh-label" for="categoriaPerfil"><i class="bi bi-bookmark"></i> Categoría</label>
                <div class="search-select">
                  <input id="categoriaPerfil" v-model="busquedaCategoria" type="text" required
                    class="form-control" placeholder="Buscar categoría..." autocomplete="off"
                    @input="onCategoriaInput" @focus="mostrarCategorias = true"
                    @blur="ocultarOpciones('categoria')" />
                  <i class="bi bi-search search-select-icon"></i>
                  <ul v-if="mostrarCategorias" class="search-select-options">
                    <li v-for="item in categoriasFiltradas" :key="item.idCategoria"
                      @mousedown.prevent="seleccionarCategoria(item)">
                      {{ item.catNombre }}
                    </li>
                    <li v-if="!categoriasFiltradas.length" class="empty">No se encontraron categorías</li>
                  </ul>
                </div>
                <small class="qh-help">Opción del menú que será visible para el perfil.</small>
              </div>

              <div>
                <label class="qh-label" for="perfilCategoria"><i class="bi bi-person-badge"></i> Perfil</label>
                <div class="search-select">
                  <input id="perfilCategoria" v-model="busquedaPerfil" type="text" required
                    class="form-control" placeholder="Buscar perfil..." autocomplete="off"
                    @input="onPerfilInput" @focus="mostrarPerfiles = true"
                    @blur="ocultarOpciones('perfil')" />
                  <i class="bi bi-search search-select-icon"></i>
                  <ul v-if="mostrarPerfiles" class="search-select-options">
                    <li v-for="item in perfilesFiltrados" :key="item.idPerfil"
                      @mousedown.prevent="seleccionarPerfil(item)">
                      {{ item.perNombre }}
                    </li>
                    <li v-if="!perfilesFiltrados.length" class="empty">No se encontraron perfiles</li>
                  </ul>
                </div>
                <small class="qh-help">Perfil que recibirá acceso a la categoría seleccionada.</small>
              </div>

              <button class="btn qh-btn qh-btn-primary association-save" :disabled="!canAssociate">
                <i class="bi bi-link-45deg"></i>
                <span>Asociar categoría</span>
              </button>
            </form>
          </div>
        </section>

        <section class="qh-panel">
          <div class="qh-panel-header">
            <div>
              <h2 class="qh-panel-title">Categorías por perfil</h2>
              <p class="qh-panel-copy">Revisa, edita o elimina las asociaciones de acceso registradas.</p>
            </div>
          </div>
          <div class="qh-panel-body">
            <TablaCatexperfil />
          </div>
        </section>
      </section>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import TablaCatexperfil from "@/components/Configuracion/Tablas/TablaCatexperfil.vue";
import TablaPerfil from "@/components/Configuracion/Tablas/tablaPerfil.vue";
import Swal from "sweetalert";
import Swal2 from "sweetalert2";

export default {
  name: "PowerbyConfiguracionAcceso",
  components: {
    TablaPerfil,
    TablaCatexperfil,
  },
  data() {
    return {
      activeTab: "perfiles",
      perfilNombre: "",
      idUsuario: "",
      idCategoria: "",
      idPerfil: "",
      usuarios: [],
      idUsuarios: "",
      busquedaCategoria: "",
      busquedaPerfil: "",
      mostrarCategorias: false,
      mostrarPerfiles: false,
    };
  },
  computed: {
    ...mapGetters("acciones", ["ListaCategorias", "ListaPerfil", "ListaCatxPerfil"]),
    totalPerfiles() {
      return Array.isArray(this.ListaPerfil) ? this.ListaPerfil.length : 0;
    },
    totalAsociaciones() {
      return Array.isArray(this.ListaCatxPerfil) ? this.ListaCatxPerfil.length : 0;
    },
    canAssociate() {
      return !!this.idCategoria && !!this.idPerfil;
    },
    categoriasFiltradas() {
      const texto = this.normalizarTexto(this.busquedaCategoria);
      return (this.ListaCategorias || []).filter((item) =>
        this.normalizarTexto(item.catNombre).includes(texto)
      );
    },
    perfilesFiltrados() {
      const texto = this.normalizarTexto(this.busquedaPerfil);
      return (this.ListaPerfil || []).filter((item) =>
        this.normalizarTexto(item.perNombre).includes(texto)
      );
    },
  },
  mounted() {
    this.idUsuario = localStorage.getItem("userId");
  },
  methods: {
    ...mapActions("acciones", ["ListarPerfil", "ListarCatxPerfil"]),
    normalizarTexto(valor) {
      return String(valor || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    onCategoriaInput() {
      this.idCategoria = "";
      this.mostrarCategorias = true;
    },
    onPerfilInput() {
      this.idPerfil = "";
      this.mostrarPerfiles = true;
    },
    seleccionarCategoria(item) {
      this.idCategoria = item.idCategoria;
      this.busquedaCategoria = item.catNombre;
      this.mostrarCategorias = false;
    },
    seleccionarPerfil(item) {
      this.idPerfil = item.idPerfil;
      this.busquedaPerfil = item.perNombre;
      this.mostrarPerfiles = false;
    },
    ocultarOpciones(tipo) {
      setTimeout(() => {
        if (tipo === "categoria") this.mostrarCategorias = false;
        if (tipo === "perfil") this.mostrarPerfiles = false;
      }, 150);
    },
    GuardarPerfil() {
      if (!this.perfilNombre.trim()) return;
      const from = {
        idUsuario: this.idUsuario,
        perNombre: this.perfilNombre,
      };

      this.axios.post(`BIClientes/RegistrarPerfil`, from).then((r) => {
        if (r.data) {
          Swal(`Se registró el perfil ${this.perfilNombre}`, "", "success");
          this.perfilNombre = "";
          this.ListarPerfil();
        } else {
          Swal2.fire({
            icon: "error",
            title: `El perfil ${this.perfilNombre} ya existe`,
            confirmButtonColor: "#0b5db7",
          });
        }
      });
    },
    CategoriaxPerfil() {
      if (!this.canAssociate) return;
      const from = {
        idUsuario: this.idUsuario,
        idCategoria: this.idCategoria,
        idPerfil: this.idPerfil,
      };

      this.axios.post(`BIClientes/RegistrarCatxPerfil`, from).then((r) => {
        if (r.data) {
          Swal(`Se registró la asociación`, "", "success");
          this.ListarCatxPerfil();
          this.idCategoria = "";
          this.idPerfil = "";
          this.busquedaCategoria = "";
          this.busquedaPerfil = "";
        } else {
          Swal2.fire({
            icon: "error",
            title: "La categoría y el perfil ya se encuentran asociados",
            confirmButtonColor: "#0b5db7",
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.access-admin-page { display: grid; gap: .8rem; }
.admin-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 1.25rem; }
.admin-kicker { display: block; margin-bottom: .35rem; color: var(--qhatu-primary); font-size: .72rem; font-weight: 850; letter-spacing: .11em; text-transform: uppercase; }
.admin-stats { display: flex; gap: .55rem; }
.stat-card { min-width: 92px; min-height: 64px; padding: .7rem .8rem; border: 1px solid var(--qhatu-border); border-radius: 14px; background: #fff; display: grid; place-items: center; box-shadow: 0 8px 18px rgba(24, 52, 86, .05); }
.stat-card strong { color: var(--qhatu-primary); font-size: 1rem; line-height: 1; }
.stat-card span { color: var(--qhatu-muted); font-size: .68rem; }
.actions-guide { min-height: 42px; padding: .55rem .75rem; border: 1px solid var(--qhatu-border); border-radius: 12px; background: #fff; display: flex; align-items: center; gap: .8rem; flex-wrap: wrap; font-size: .72rem; color: #586a81; }
.actions-guide strong { margin-right: .15rem; color: #1f3047; font-size: .72rem; }
.actions-guide span { display: inline-flex; align-items: center; gap: .25rem; }
.admin-tabs { width: max-content; max-width: 100%; padding: .3rem; border: 1px solid var(--qhatu-border); border-radius: 14px; background: #fff; display: flex; gap: .3rem; overflow-x: auto; }
.admin-tabs button { min-height: 44px; border: 0; border-radius: 11px; background: transparent; color: #62738a; display: inline-flex; align-items: center; gap: .5rem; padding: .65rem .9rem; font-weight: 750; white-space: nowrap; }
.admin-tabs button small { min-width: 26px; height: 24px; padding: 0 .45rem; border-radius: 999px; background: #eef2f7; display: grid; place-items: center; font-size: .68rem; }
.admin-tabs button.active { color: #fff; background: linear-gradient(135deg, #0b5db7, #087fc0); box-shadow: 0 8px 18px rgba(11, 93, 183, .2); }
.admin-tabs button.active small { color: #fff; background: rgba(255,255,255,.18); }
.tab-content-panel { display: grid; gap: .75rem; }
.association-panel { position: relative; z-index: 2; overflow: visible; }
.profile-form { display: grid; grid-template-columns: minmax(0, 1fr) minmax(230px, .55fr); align-items: start; gap: .85rem; }
.profile-field { display: grid; }
.qh-label { display: flex; align-items: center; gap: .45rem; margin-bottom: .42rem; color: #273950; font-size: .82rem; font-weight: 800; text-transform: uppercase; }
.qh-label i { color: #6a7c91; }
.qh-help { display: inline-block; margin-top: .35rem; color: var(--qhatu-muted); font-size: .75rem; }
.profile-save { min-height: 54px; margin-top: 1.62rem; }
.association-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)) minmax(230px, .55fr); align-items: start; gap: .85rem; }
.association-save { min-height: 44px; height: 44px; align-self: start; margin-top: 1.62rem; }
.search-select { position: relative; }
.search-select .form-control { padding-right: 2.75rem; }
.search-select-icon { position: absolute; top: 50%; right: 1rem; transform: translateY(-50%); color: #718096; pointer-events: none; }
.search-select-options { position: absolute; z-index: 40; top: calc(100% + 5px); left: 0; right: 0; max-height: 220px; overflow-y: auto; margin: 0; padding: .35rem; list-style: none; border: 1px solid #d5e1ee; border-radius: 12px; background: #fff; box-shadow: 0 14px 30px rgba(15, 23, 42, .14); }
.search-select-options li { padding: .65rem .75rem; border-radius: 9px; color: #334155; cursor: pointer; }
.search-select-options li:hover { background: #eaf4ff; color: var(--qhatu-primary); }
.search-select-options li.empty { color: #718096; cursor: default; }
.search-select-options li.empty:hover { background: transparent; color: #718096; }
.form-control, .form-select { min-height: 54px; border-radius: 13px; }
button:disabled { opacity: .65; cursor: not-allowed; }
@media (max-width: 991.98px) {
  .admin-header { align-items: flex-start; }
  .association-form { grid-template-columns: 1fr 1fr; }
  .association-save { grid-column: 1 / -1; width: 100%; margin-top: 0; }
}
@media (max-width: 767.98px) {
  .admin-header { flex-direction: column; }
  .admin-stats { width: 100%; }
  .stat-card { flex: 1; }
  .actions-guide { align-items: flex-start; }
  .admin-tabs { width: 100%; }
  .admin-tabs button { flex: 1; justify-content: center; }
  .profile-form, .association-form { grid-template-columns: 1fr; }
  .profile-save, .association-save { width: 100%; margin-top: 0; }
}
</style>
